import { useMemo, useState } from "react";
import { btn } from "../styles/shared";
import { ingredientGrams } from "../utils/calculations";

const PROTEIN_KEYWORDS = [
  "chicken", "beef", "tuna", "salmon", "egg", "protein", "tofu", "lentil",
  "bean", "turkey", "pork", "shrimp", "cod", "tilapia", "greek yogurt",
  "cottage cheese", "tempeh", "seitan", "edamame", "quinoa",
];

const PERIODS = [
  ["week", "7 days", 7],
  ["month", "30 days", 30],
  ["quarter", "90 days", 90],
];

function isProteinFood(name) {
  const n = String(name || "").toLowerCase();
  return PROTEIN_KEYWORDS.some(k => n.includes(k));
}

function gramsLabel(grams) {
  const value = Math.round(Number(grams) || 0);
  if (value >= 1000) {
    const kg = value / 1000;
    return `${kg >= 10 ? Math.round(kg) : kg.toFixed(1)}kg`;
  }
  return `${value}g`;
}

function csvValue(value) {
  const text = String(value ?? "");
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

function downloadCsv(filename, rows) {
  const csv = rows.map(row => row.map(csvValue).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function sortUsageItems(a, b) {
  if (a.isSingleUse !== b.isSingleUse) return a.isSingleUse ? 1 : -1;
  if (a.daysUsed !== b.daysUsed) return b.daysUsed - a.daysUsed;
  if (a.useCount !== b.useCount) return b.useCount - a.useCount;
  if (a.totalG !== b.totalG) return b.totalG - a.totalG;
  return a.name.localeCompare(b.name);
}

function aggregateUsage(logs, days) {
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - days + 1);
  const cutoffStr = cutoff.toISOString().slice(0, 10);
  const totals = {};
  const loggedDates = new Set();

  for (const day of logs) {
    if (!day?.date || day.date < cutoffStr) continue;
    const meals = Object.values(day.meals || {});
    if (meals.length) loggedDates.add(day.date);

    for (const meal of meals) {
      for (const ing of meal.ingredients || []) {
        if (!ing?.name) continue;
        const key = ing.name.toLowerCase().trim();
        const grams = ingredientGrams(ing);
        if (!key || grams <= 0) continue;
        if (!totals[key]) {
          totals[key] = {
            key,
            name: ing.name,
            totalG: 0,
            useCount: 0,
            days: new Set(),
          };
        }
        totals[key].totalG += grams;
        totals[key].useCount += 1;
        totals[key].days.add(day.date);
      }
    }
  }

  const items = Object.values(totals)
    .map(item => {
      const totalG = Math.round(item.totalG);
      const daysUsed = item.days.size;
      const avgPerUse = item.useCount > 0 ? totalG / item.useCount : 0;
      const avgPerLoggedDay = loggedDates.size > 0 ? totalG / loggedDates.size : 0;
      return {
        name: item.name,
        totalG,
        useCount: item.useCount,
        daysUsed,
        avgPerUse: Math.round(avgPerUse),
        avgPerLoggedDay: Math.round(avgPerLoggedDay),
        isProtein: isProteinFood(item.name),
        isSingleUse: item.useCount <= 1,
      };
    })
    .sort(sortUsageItems);

  return { items, logDays: loggedDates.size };
}

function buildShoppingList(items, periodKey) {
  return items.map(item => {
    const base = periodKey === "week"
      ? item.totalG
      : periodKey === "month"
      ? item.totalG
      : Math.round(item.totalG / 3);
    const roundTo = item.totalG >= 1000 ? 500 : 100;
    return {
      ...item,
      suggestBuy: Math.ceil(Math.max(base, item.avgPerUse) / roundTo) * roundTo,
    };
  });
}

function getTips(items, consistency, logDays) {
  const repeatItems = items.filter(item => !item.isSingleUse);
  const tips = [];
  if (repeatItems[0]) {
    tips.push(`${repeatItems[0].name} is your strongest staple at ${gramsLabel(repeatItems[0].totalG)} across ${repeatItems[0].useCount} uses.`);
  }
  if (consistency < 0.5) {
    tips.push(`Only ${logDays} logged day${logDays === 1 ? "" : "s"} are in this window, so supply estimates should be treated as directional.`);
  }
  if (repeatItems.filter(item => item.isProtein).length < 2) {
    tips.push("Protein variety is low in the logged supply pattern.");
  }
  if (repeatItems.length >= 5) {
    tips.push(`Top staples: ${repeatItems.slice(0, 5).map(item => item.name).join(", ")}.`);
  }
  return tips.slice(0, 4);
}

export default function Supply({ logs = [], showToast }) {
  const [tab, setTab] = useState("items");
  const [period, setPeriod] = useState("month");

  const periodMeta = PERIODS.find(([key]) => key === period) || PERIODS[1];
  const days = periodMeta[2];
  const { items, logDays } = useMemo(() => aggregateUsage(logs, days), [logs, days]);
  const regularItems = items.filter(item => !item.isSingleUse);
  const singleUseItems = items.filter(item => item.isSingleUse);
  const topTotal = Math.max(1, ...items.map(item => item.totalG));
  const totalKg = items.reduce((sum, item) => sum + item.totalG, 0) / 1000;
  const consistency = logDays / days;
  const shoppingList = useMemo(() => buildShoppingList(items, period), [items, period]);
  const tips = useMemo(() => getTips(items, consistency, logDays), [items, consistency, logDays]);

  function exportUsage() {
    const rows = [
      ["rank", "section", "name", "total_g", "total", "uses", "days_used", "avg_g_per_use", "avg_g_per_logged_day", "protein", "period_days"],
      ...items.map((item, index) => [
        index + 1,
        item.isSingleUse ? "single_use" : "regular",
        item.name,
        item.totalG,
        gramsLabel(item.totalG),
        item.useCount,
        item.daysUsed,
        item.avgPerUse,
        item.avgPerLoggedDay,
        item.isProtein ? "yes" : "no",
        days,
      ]),
    ];
    downloadCsv(`sublime_supply_usage_${period}_${new Date().toISOString().slice(0, 10)}.csv`, rows);
    showToast?.("Supply usage exported");
  }

  function exportShopping() {
    const rows = [
      ["rank", "section", "name", "buy_g", "buy", "used_g", "uses", "days_used", "period_days"],
      ...shoppingList.map((item, index) => [
        index + 1,
        item.isSingleUse ? "single_use" : "regular",
        item.name,
        item.suggestBuy,
        gramsLabel(item.suggestBuy),
        item.totalG,
        item.useCount,
        item.daysUsed,
        days,
      ]),
    ];
    downloadCsv(`sublime_supply_shopping_${period}_${new Date().toISOString().slice(0, 10)}.csv`, rows);
    showToast?.("Shopping list exported");
  }

  const card = {
    background: "linear-gradient(145deg,rgba(5,24,19,0.96),rgba(9,11,24,0.96))",
    border: "1px solid rgba(52,211,153,0.18)",
    borderRadius: 14,
    boxShadow: "0 4px 22px rgba(0,0,0,0.32)",
  };
  const controlBtn = (active) => ({
    ...btn,
    background: active ? "rgba(52,211,153,0.16)" : "rgba(255,255,255,0.04)",
    color: active ? "#6ee7b7" : "#94a3b8",
    border: "1px solid " + (active ? "rgba(52,211,153,0.35)" : "rgba(255,255,255,0.08)"),
    padding: "8px 12px",
    fontSize: 12,
    borderRadius: 8,
    whiteSpace: "nowrap",
  });

  const statBox = (label, value, color = "#e2e8f0") => (
    <div style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 10, padding: "12px 13px", minWidth: 0 }}>
      <div style={{ fontSize: 9, fontFamily: "'Space Mono',monospace", color: "#7f8ea3", letterSpacing: 1.1, textTransform: "uppercase", marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 800, color, fontFamily: "'Space Mono',monospace", overflowWrap: "anywhere" }}>{value}</div>
    </div>
  );

  function renderItemRow(item, index, muted = false) {
    const pct = Math.max(4, Math.round((item.totalG / topTotal) * 100));
    return (
      <div key={`${item.name}-${index}`} style={{ padding: "13px 0", borderBottom: "1px solid rgba(255,255,255,0.055)" }}>
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) auto", gap: 12, alignItems: "start" }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
              <span style={{ width: 26, color: muted ? "#475569" : "#34d399", fontFamily: "'Space Mono',monospace", fontSize: 12 }}>{String(index + 1).padStart(2, "0")}</span>
              <span style={{ color: muted ? "#94a3b8" : "#f8fafc", fontSize: 14, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.name}</span>
              {item.isProtein && <span style={{ color: "#fb923c", border: "1px solid rgba(251,146,60,0.28)", borderRadius: 7, padding: "2px 6px", fontSize: 10 }}>protein</span>}
              {item.isSingleUse && <span style={{ color: "#64748b", border: "1px solid rgba(148,163,184,0.18)", borderRadius: 7, padding: "2px 6px", fontSize: 10 }}>single</span>}
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 6, color: muted ? "#64748b" : "#8aa3b8", fontSize: 12 }}>
              <span>{item.useCount} use{item.useCount === 1 ? "" : "s"}</span>
              <span>{item.daysUsed} day{item.daysUsed === 1 ? "" : "s"}</span>
              <span>{gramsLabel(item.avgPerUse)} avg/use</span>
              <span>{gramsLabel(item.avgPerLoggedDay)} avg/logged day</span>
            </div>
          </div>
          <div style={{ textAlign: "right", color: muted ? "#94a3b8" : "#6ee7b7", fontFamily: "'Space Mono',monospace", fontSize: 18, fontWeight: 800 }}>
            {gramsLabel(item.totalG)}
          </div>
        </div>
        <div style={{ marginLeft: 34, marginTop: 9, height: 5, borderRadius: 999, background: "rgba(255,255,255,0.06)", overflow: "hidden" }}>
          <div style={{ width: `${pct}%`, height: "100%", background: muted ? "#334155" : item.isProtein ? "#fb923c" : "#34d399", borderRadius: 999 }} />
        </div>
      </div>
    );
  }

  function renderShoppingRow(item, index) {
    return (
      <div key={`${item.name}-${index}`} style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) auto", gap: 14, alignItems: "center", padding: "12px 0", borderBottom: "1px solid rgba(255,255,255,0.055)" }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ color: item.isSingleUse ? "#94a3b8" : "#f8fafc", fontWeight: 700, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.name}</div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 5, color: "#64748b", fontSize: 12 }}>
            <span>used {gramsLabel(item.totalG)}</span>
            <span>{item.useCount} use{item.useCount === 1 ? "" : "s"}</span>
            {item.isSingleUse && <span>single use</span>}
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ color: item.isSingleUse ? "#94a3b8" : "#fb923c", fontFamily: "'Space Mono',monospace", fontWeight: 800, fontSize: 17 }}>{gramsLabel(item.suggestBuy)}</div>
          <div style={{ color: "#475569", fontSize: 11 }}>buy</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 14 }}>
        <div>
          <h1 className="page-title" style={{ fontSize: 28, fontWeight: 800, color: "#f8fafc", margin: 0 }}>Supply</h1>
          <p style={{ fontSize: 13, color: "#94a3b8", margin: "6px 0 0" }}>
            {logDays} logged day{logDays === 1 ? "" : "s"} in the last {days} days
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          {PERIODS.map(([key, label]) => (
            <button key={key} style={controlBtn(period === key)} onClick={() => setPeriod(key)}>{label}</button>
          ))}
          <button style={{ ...controlBtn(false), color: "#f8fafc" }} onClick={tab === "shopping" ? exportShopping : exportUsage}>Export CSV</button>
        </div>
      </div>

      <div style={{ ...card, padding: 16 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(125px,1fr))", gap: 10 }}>
          {statBox("Tracked", items.length, "#f8fafc")}
          {statBox("Repeat", regularItems.length, "#34d399")}
          {statBox("Single Use", singleUseItems.length, "#94a3b8")}
          {statBox("Total", `${totalKg >= 10 ? totalKg.toFixed(0) : totalKg.toFixed(1)}kg`, "#fb923c")}
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button style={controlBtn(tab === "items")} onClick={() => setTab("items")}>Used Items</button>
        <button style={controlBtn(tab === "shopping")} onClick={() => setTab("shopping")}>Buy List</button>
        <button style={controlBtn(tab === "tips")} onClick={() => setTab("tips")}>Tips</button>
      </div>

      {tab === "items" && (
        <div style={{ ...card, padding: "16px 20px" }}>
          {items.length === 0 ? (
            <p style={{ color: "#64748b", fontSize: 14, textAlign: "center", padding: "30px 0" }}>No meal logs in this window.</p>
          ) : (
            <>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10, marginBottom: 6 }}>
                <div style={{ fontSize: 11, color: "#94a3b8", fontFamily: "'Space Mono',monospace", letterSpacing: 1.2, textTransform: "uppercase" }}>Most Used</div>
              </div>
              {regularItems.map((item, index) => renderItemRow(item, index))}
              {singleUseItems.length > 0 && (
                <div style={{ marginTop: 18 }}>
                  <div style={{ fontSize: 11, color: "#64748b", fontFamily: "'Space Mono',monospace", letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 3 }}>Single Use</div>
                  {singleUseItems.map((item, index) => renderItemRow(item, regularItems.length + index, true))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {tab === "shopping" && (
        <div style={{ ...card, padding: "16px 20px" }}>
          {shoppingList.length === 0 ? (
            <p style={{ color: "#64748b", fontSize: 14, textAlign: "center", padding: "30px 0" }}>No shopping data in this window.</p>
          ) : (
            <>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10, marginBottom: 6 }}>
                <div style={{ fontSize: 11, color: "#94a3b8", fontFamily: "'Space Mono',monospace", letterSpacing: 1.2, textTransform: "uppercase" }}>Buy List</div>
                <div style={{ fontSize: 11, color: "#64748b" }}>{periodMeta[1]} usage basis</div>
              </div>
              {shoppingList.map(renderShoppingRow)}
            </>
          )}
        </div>
      )}

      {tab === "tips" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {tips.length === 0 ? (
            <div style={{ ...card, padding: 24, textAlign: "center", color: "#64748b", fontSize: 14 }}>No supply tips yet.</div>
          ) : tips.map((tip, i) => (
            <div key={i} style={{ ...card, padding: "16px 18px", borderLeft: "3px solid #34d399", borderRadius: "0 14px 14px 0" }}>
              <p style={{ fontSize: 14, color: "#cbd5e1", lineHeight: 1.55, margin: 0 }}>{tip}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
